"use client"

import { useState, useRef, useEffect } from "react"
import type { UpgradeState, UpgradeColumn, UpgradeDefinition } from "@/types/upgrade.types"
import { CloseIcon } from "@/components/icons/pixel-icons"
import { UpgradeTreeNode } from "./upgrade-tree-node"
import { UpgradeBottomSheet } from "./upgrade-bottom-sheet"
import {
  getRootUpgrade,
  getBranchUpgrades,
  columnLabels,
  columnDescriptions,
} from "@/data/upgrade-tree"
import {
  getUpgradeNodeStatus,
  canPurchaseUpgrade,
  getPurchaseCount,
  getUpgradeCost,
} from "@/systems/upgrade-system"
import { playUpgradeTreeUpgradeSound } from "@/hooks/use-sound-effects"

interface UpgradeTreeWindowProps {
  money: number
  upgradeState: UpgradeState
  onUnlock: (upgradeId: string, cost: number) => void
  onClose: () => void
  isMobile?: boolean
}

const COLUMNS: UpgradeColumn[] = ["military", "arcane", "command"]

export const UpgradeTreeWindow = ({
  money,
  upgradeState,
  onUnlock,
  onClose,
  isMobile = false,
}: UpgradeTreeWindowProps) => {
  const [activeTab, setActiveTab] = useState<UpgradeColumn>("military")
  const [selectedUpgrade, setSelectedUpgrade] = useState<UpgradeDefinition | null>(null)
  const [justUnlockedIds, setJustUnlockedIds] = useState<Set<string>>(new Set())
  const prevPurchaseCounts = useRef<Record<string, number>>(upgradeState.purchaseCounts)

  // Track newly purchased upgrades (any new purchase triggers effect)
  useEffect(() => {
    const newPurchases: string[] = []
    for (const [id, count] of Object.entries(upgradeState.purchaseCounts)) {
      const prevCount = prevPurchaseCounts.current[id] ?? 0
      if (count > prevCount) {
        newPurchases.push(id)
      }
    }
    if (newPurchases.length > 0) {
      setJustUnlockedIds((prev) => new Set([...prev, ...newPurchases]))
      // Clear effect after animation
      setTimeout(() => {
        setJustUnlockedIds((prev) => {
          const next = new Set(prev)
          for (const id of newPurchases) {
            next.delete(id)
          }
          return next
        })
      }, 500)
    }
    prevPurchaseCounts.current = upgradeState.purchaseCounts
  }, [upgradeState.purchaseCounts])

  const handleNodeSelect = (upgrade: UpgradeDefinition) => {
    if (isMobile) {
      setSelectedUpgrade(upgrade)
    } else {
      // Desktop: directly purchase if available/unlocked and affordable
      if (canPurchaseUpgrade(upgrade.id, upgradeState, money)) {
        const purchaseCount = getPurchaseCount(upgrade.id, upgradeState)
        const cost = getUpgradeCost(upgrade, purchaseCount)
        playUpgradeTreeUpgradeSound()
        onUnlock(upgrade.id, cost)
      }
    }
  }

  const handleMobileUnlock = () => {
    if (selectedUpgrade && canPurchaseUpgrade(selectedUpgrade.id, upgradeState, money)) {
      const purchaseCount = getPurchaseCount(selectedUpgrade.id, upgradeState)
      const cost = getUpgradeCost(selectedUpgrade, purchaseCount)
      playUpgradeTreeUpgradeSound()
      onUnlock(selectedUpgrade.id, cost)
      setSelectedUpgrade(null)
    }
  }

  const renderColumn = (column: UpgradeColumn) => {
    const rootUpgrade = getRootUpgrade(column)
    const branchA = getBranchUpgrades(column, "a")
    const branchB = getBranchUpgrades(column, "b")

    if (!rootUpgrade) return null

    return (
      <div className="flex flex-col items-center">
        {/* Column header */}
        <div className="text-center mb-4">
          <h3 className={`text-base md:text-lg font-bold ${
            column === "military"
              ? "text-blue-400"
              : column === "arcane"
              ? "text-purple-400"
              : "text-orange-400"
          }`}>{columnLabels[column]}</h3>
          <p className="text-[10px] md:text-xs text-slate-400">{columnDescriptions[column]}</p>
        </div>

        {/* Both branches side by side, going upward */}
        <div className="flex gap-6 md:gap-4 mb-2">
          {/* Branch A (left) - reversed so tier 3 is at top */}
          <div className="flex flex-col-reverse items-center gap-2">
            {branchA.map((upgrade, index) => {
              const status = getUpgradeNodeStatus(upgrade, upgradeState)
              const purchaseCount = getPurchaseCount(upgrade.id, upgradeState)
              const nextCost = getUpgradeCost(upgrade, purchaseCount)
              const canAfford = money >= nextCost
              return (
                <div key={upgrade.id} className="relative">
                  <UpgradeTreeNode
                    upgrade={upgrade}
                    status={status}
                    canAfford={canAfford}
                    purchaseCount={purchaseCount}
                    onSelect={handleNodeSelect}
                    isMobile={isMobile}
                    isJustUnlocked={justUnlockedIds.has(upgrade.id)}
                  />
                  {/* Connection line to next tier */}
                  {index < branchA.length - 1 && (
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0.5 h-4 bg-slate-600/50" />
                  )}
                </div>
              )
            })}
          </div>

          {/* Branch B (right) - reversed so tier 3 is at top */}
          <div className="flex flex-col-reverse items-center gap-2">
            {branchB.map((upgrade, index) => {
              const status = getUpgradeNodeStatus(upgrade, upgradeState)
              const purchaseCount = getPurchaseCount(upgrade.id, upgradeState)
              const nextCost = getUpgradeCost(upgrade, purchaseCount)
              const canAfford = money >= nextCost
              return (
                <div key={upgrade.id} className="relative">
                  <UpgradeTreeNode
                    upgrade={upgrade}
                    status={status}
                    canAfford={canAfford}
                    purchaseCount={purchaseCount}
                    onSelect={handleNodeSelect}
                    isMobile={isMobile}
                    isJustUnlocked={justUnlockedIds.has(upgrade.id)}
                  />
                  {/* Connection line to next tier */}
                  {index < branchB.length - 1 && (
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0.5 h-4 bg-slate-600/50" />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Diagonal lines from branches to root */}
        <div className="relative h-6 flex items-center justify-center" style={{ width: isMobile ? "152px" : "136px" }}>
          <svg className="absolute inset-0" viewBox="0 0 100 24" preserveAspectRatio="none">
            {/* Left branch to center - from bottom-center of left node to top-center of root */}
            <path
              d={isMobile ? "M21 0 L50 24" : "M24 0 L50 24"}
              stroke="rgba(100, 116, 139, 0.5)"
              strokeWidth="2"
              fill="none"
            />
            {/* Right branch to center - from bottom-center of right node to top-center of root */}
            <path
              d={isMobile ? "M79 0 L50 24" : "M76 0 L50 24"}
              stroke="rgba(100, 116, 139, 0.5)"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        </div>

        {/* Root upgrade (at bottom) */}
        <div className="relative">
          {(() => {
            const rootPurchaseCount = getPurchaseCount(rootUpgrade.id, upgradeState)
            const rootNextCost = getUpgradeCost(rootUpgrade, rootPurchaseCount)
            return (
              <UpgradeTreeNode
                upgrade={rootUpgrade}
                status={getUpgradeNodeStatus(rootUpgrade, upgradeState)}
                canAfford={money >= rootNextCost}
                purchaseCount={rootPurchaseCount}
                onSelect={handleNodeSelect}
                isMobile={isMobile}
                isJustUnlocked={justUnlockedIds.has(rootUpgrade.id)}
              />
            )
          })()}
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-[101] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Window */}
      <div className={`fixed inset-0 flex items-center justify-center z-[102] pointer-events-none ${isMobile ? "p-0" : "p-4"}`}>
        <div className={`bg-gradient-to-b from-slate-900/98 to-slate-950/98 border border-slate-600/50 shadow-2xl max-w-4xl w-full pointer-events-auto flex flex-col ${isMobile ? "h-full rounded-none" : "max-h-[90vh] rounded-xl"}`}>
          {/* Header */}
          <div className="relative p-4 border-b border-slate-700/50 shrink-0">
            {/* Gold display - smaller on mobile */}
            <div className={`absolute top-3 left-3 flex items-center bg-black/30 rounded-lg ${isMobile ? "gap-1.5 px-2 py-1" : "gap-2 px-3 py-1.5"}`}>
              <img
                src="/images/icon-coins.png"
                alt="gold"
                className={isMobile ? "w-4 h-4" : "w-5 h-5"}
                style={{ imageRendering: "pixelated" }}
              />
              <span className={`text-game-gold font-bold ${isMobile ? "text-sm" : ""}`}>{money}</span>
            </div>

            {/* Header text and close button - flex row aligned right */}
            <div className="flex items-center justify-end gap-2">
              <p className="text-slate-400 text-xs">
                {isMobile ? "Lasts entire run" : "Upgrades last until the end of your run"}
              </p>
              <button
                onClick={onClose}
                className="p-1 text-slate-400 hover:text-white transition"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>

          {/* Mobile tabs */}
          {isMobile && (
            <div className="flex border-b border-slate-700/50 shrink-0">
              {COLUMNS.map((column) => {
                const bgImage = column === "military"
                  ? "/images/techtree/techtree_archerbarracks.png"
                  : column === "arcane"
                  ? "/images/techtree/techtree_magesiege.png"
                  : "/images/techtree/techtree_hero.png"

                const textColor = column === "military"
                  ? "text-blue-400"
                  : column === "arcane"
                  ? "text-purple-400"
                  : "text-orange-400"

                const borderColor = column === "military"
                  ? "border-blue-400"
                  : column === "arcane"
                  ? "border-purple-400"
                  : "border-orange-400"

                const isActive = activeTab === column

                return (
                  <button
                    key={column}
                    onClick={() => setActiveTab(column)}
                    className={`flex-1 py-3 text-sm font-bold transition-colors relative overflow-hidden ${
                      isActive
                        ? `${textColor} ${borderColor} border-b-2`
                        : "text-slate-400 hover:text-slate-300"
                    }`}
                    style={{
                      backgroundImage: `url(${bgImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    {/* Dark overlay - 50% for active, 70% for inactive */}
                    <span className={`absolute inset-0 ${isActive ? "bg-black/50" : "bg-black/70"}`} />
                    <span className="relative">{columnLabels[column]}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Content - pt-16 gives space for top-row tooltips to render without being clipped */}
          <div
            className="flex-1 overflow-auto relative pt-16"
            style={isMobile ? {
              backgroundImage: `url(${
                activeTab === "military"
                  ? "/images/techtree/techtree_archerbarracks.png"
                  : activeTab === "arcane"
                  ? "/images/techtree/techtree_magesiege.png"
                  : "/images/techtree/techtree_hero.png"
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            } : undefined}
          >
            {/* Dark overlay for mobile */}
            {isMobile && <div className="absolute inset-0 bg-black/75" />}

            {isMobile ? (
              // Mobile: single column based on active tab, vertically centered, scaled up
              <div
                className="relative flex items-center justify-center min-h-full p-4"
                style={{ transform: "scale(1.15)", transformOrigin: "center center" }}
              >
                {renderColumn(activeTab)}
              </div>
            ) : (
              // Desktop: all 3 columns with background images, taking full height
              <div className="grid grid-cols-3 min-h-full">
                {COLUMNS.map((column) => {
                  const bgImage = column === "military"
                    ? "/images/techtree/techtree_archerbarracks.png"
                    : column === "arcane"
                    ? "/images/techtree/techtree_magesiege.png"
                    : "/images/techtree/techtree_hero.png"

                  return (
                    <div
                      key={column}
                      className="relative flex flex-col"
                      style={{
                        backgroundImage: `url(${bgImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      {/* Dark overlay for readability */}
                      <div className="absolute inset-0 bg-black/75" />
                      <div className="relative p-4 flex-1 flex flex-col justify-end">
                        {renderColumn(column)}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="p-4 border-t border-slate-700/50 flex-shrink-0">
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-md bg-blue-600 border border-blue-400 flex items-center justify-center">
                  <span className="text-[8px] text-white font-bold">3</span>
                </div>
                <span>Purchase Level</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-4 rounded-md bg-amber-600 border border-amber-400 flex items-center justify-center px-0.5">
                  <span className="text-[7px] text-white font-bold">MAX</span>
                </div>
                <span>Fully Upgraded</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500 border border-purple-300 flex items-center justify-center">
                  <span className="text-[6px] text-white font-bold">A</span>
                </div>
                <span>Active Ability</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom sheet */}
      {isMobile && (() => {
        const selectedPurchaseCount = selectedUpgrade
          ? getPurchaseCount(selectedUpgrade.id, upgradeState)
          : 0
        const selectedNextCost = selectedUpgrade
          ? getUpgradeCost(selectedUpgrade, selectedPurchaseCount)
          : 0
        return (
          <UpgradeBottomSheet
            upgrade={selectedUpgrade}
            status={
              selectedUpgrade
                ? getUpgradeNodeStatus(selectedUpgrade, upgradeState)
                : "locked"
            }
            canAfford={selectedUpgrade ? money >= selectedNextCost : false}
            purchaseCount={selectedPurchaseCount}
            onUnlock={handleMobileUnlock}
            onClose={() => setSelectedUpgrade(null)}
          />
        )
      })()}
    </>
  )
}
